import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { Copy, TrendingUp, Check } from "lucide-react";

export default function Hero() {
  const contract = "8gHPxqgHj6JQ2sQtMSghQYVN5qRP8wm5T6HNejuwpump";
  const [showToast, setShowToast] = useState(false);
  const [apyText, setApyText] = useState("23%+ APY");
  const toastTimeoutRef = useRef(null);

  // Fetch live APY
  useEffect(() => {
    fetch("/project/api/staking-apy")
      .then(r => r.json())
      .then(d => {
        const avg = d?.data?.averageApy;
        if (avg && avg > 0) {
          setApyText(`${Math.floor(avg)}%+ APY`);
        }
      })
      .catch(() => {});
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);
  
  // Detect mobile for reduced animations (only check once)
  const isMobile = useMemo(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 768 || /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  }, []);
  
  // Memoize copy function to prevent re-renders
  const copyAddress = useCallback(async () => {
    // Clear any existing timeout
    if (toastTimeoutRef.current) {
      clearTimeout(toastTimeoutRef.current);
    }
    
    try {
      await navigator.clipboard.writeText(contract);
      setShowToast(true);
      toastTimeoutRef.current = setTimeout(() => {
        setShowToast(false);
        toastTimeoutRef.current = null;
      }, 3000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = contract;
      textArea.style.position = "fixed";
      textArea.style.opacity = "0";
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setShowToast(true);
      toastTimeoutRef.current = setTimeout(() => {
        setShowToast(false);
        toastTimeoutRef.current = null;
      }, 3000);
    }
  }, [contract]);


  return (
    <section
      id="hero"
      className="relative flex flex-col items-center justify-center text-center min-h-screen bg-[#0b0b0f] overflow-hidden text-gray-200 pt-4"
    >
      {/* --- Background Lighting --- */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(147,51,234,0.12),transparent_70%)]" />
      {/* Reduced blur on mobile for performance */}
      <div className={`absolute w-[700px] h-[700px] bg-purple-700/10 ${isMobile ? 'blur-xl' : 'blur-3xl'} rounded-full top-[-200px] left-[-200px]`} />
      <div className={`absolute w-[600px] h-[600px] bg-pink-600/10 ${isMobile ? 'blur-xl' : 'blur-3xl'} rounded-full bottom-[-200px] right-[-200px]`} />

      {/* --- Animated Logo --- */}
      <img
        src="/project/logo192.png"
        alt="RYS Logo"
        className="w-24 md:w-28 mb-6 z-10 drop-shadow-[0_0_20px_rgba(236,72,153,0.4)] animate-fade-in-up"
        style={{ 
          animationDuration: '1s',
          WebkitBackfaceVisibility: 'hidden',
        }}
        loading="eager"
        fetchPriority="high"
      />

      {/* --- Hero Title --- */}
      <h1
        className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-tight sm:leading-snug z-10 px-4 animate-fade-in-up"
        style={{ 
          animationDelay: '0.2s',
          animationDuration: '1s',
          animationFillMode: 'both',
          WebkitBackfaceVisibility: 'hidden',
        }}
      >
        Powering{" "}
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-500 to-pink-500">
          Solana
        </span>{" "}
        with Next-Gen Utilities
      </h1>

      {/* --- Subtext --- */}
      <p
        className="mt-4 sm:mt-6 text-gray-400 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed px-4 animate-fade-in"
        style={{ 
          animationDelay: '0.4s',
          animationDuration: '1s',
          animationFillMode: 'both',
          WebkitBackfaceVisibility: 'hidden',
        }}
      >
        From automated refunds to trading bots, scanners, and portfolio tools —{" "}
        <span className="text-gray-200 font-medium">RYS</span> is building a
        complete ecosystem of DeFi utilities on{" "}
        <span className="text-pink-400">Solana</span>. Designed for power users,
        creators, and communities.
      </p>

      {/* --- Contract Box --- */}
      <div
        onClick={copyAddress}
        className="mt-10 mx-4 sm:mx-0 px-3 sm:px-4 py-2.5 bg-black/40 border border-purple-700/40 rounded-2xl backdrop-blur-md flex items-center gap-2 sm:gap-3 
        hover:border-pink-500/50 hover:shadow-[0_0_15px_rgba(236,72,153,0.2)] transition-all cursor-pointer group max-w-full touch-manipulation"
        style={{ 
          opacity: isMobile ? 1 : undefined,
          transform: isMobile ? 'none' : undefined,
          willChange: 'transform'
        }}
      >
        <code className="text-pink-400 text-xs sm:text-sm md:text-base select-all truncate flex-1 min-w-0 font-mono">
          {contract}
        </code>
        <div className="flex-shrink-0 p-1.5 sm:p-2 rounded-md hover:bg-purple-800/20 transition active:scale-95 touch-manipulation">
          <Copy size={16} className="sm:w-[18px] sm:h-[18px] text-gray-400 group-hover:text-pink-400 transition-colors pointer-events-none" />
        </div>
      </div>

      {/* Toast Notification */}
      {showToast && (
        <div
          className="fixed top-20 left-1/2 z-50 animate-toast-in"
          style={{ 
            transform: 'translateX(-50%)',
            WebkitBackfaceVisibility: 'hidden',
          }}
        >
          <div className="px-6 py-4 bg-gradient-to-r from-purple-600/95 to-pink-600/95 border border-purple-400/50 rounded-2xl shadow-2xl shadow-purple-500/50 flex items-center gap-3 min-w-[200px]">
            <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center animate-scale-in">
              <Check size={16} className="text-white" />
            </div>
            <span className="text-white font-semibold text-sm sm:text-base">
              Copied to clipboard!
            </span>
          </div>
        </div>
      )}

      {/* --- Action Buttons --- */}
      <div
        className="flex flex-wrap justify-center gap-3 sm:gap-4 mt-10 z-10 px-4 animate-fade-in"
        style={{ 
          animationDelay: '0.8s',
          animationDuration: '1s',
          animationFillMode: 'both',
          WebkitBackfaceVisibility: 'hidden',
        }}
      >
        {/* Buy Button */}
        <a
          href="https://swap.pump.fun/?output=8gHPxqgHj6JQ2sQtMSghQYVN5qRP8wm5T6HNejuwpump"
          target="_blank"
          rel="noopener noreferrer"
          className="px-6 sm:px-8 py-2.5 sm:py-3 rounded-full bg-gradient-to-r from-purple-600 to-pink-500 
          text-white text-base sm:text-lg font-semibold shadow-[0_0_20px_rgba(236,72,153,0.4)] 
          hover:shadow-[0_0_25px_rgba(236,72,153,0.6)] hover:-translate-y-0.5 
          transition-transform whitespace-nowrap transform-gpu"
        >
          Buy $RYS
        </a>

        {/* Explore Button */}
        <a
          href="#utilities"
          className="px-6 sm:px-8 py-2.5 sm:py-3 rounded-full border border-pink-500 text-pink-400 text-base sm:text-lg font-semibold 
          hover:bg-gradient-to-r hover:from-purple-600 hover:to-pink-500 hover:text-white 
          hover:-translate-y-0.5 transition-all whitespace-nowrap transform-gpu"
        >
          Explore Utilities
        </a>
      </div>

      {/* --- Staking CTA --- */}
      <a
        href="https://refundyoursol.com/staking"
        target="_blank"
        rel="noopener noreferrer"
        className="mt-12 group relative z-10 mx-4 sm:mx-0 animate-fade-in-up"
        style={{ 
          animationDelay: '1s',
          animationDuration: '0.8s',
          animationFillMode: 'both',
          WebkitBackfaceVisibility: 'hidden',
        }}
      >
        <div className="relative px-4 sm:px-6 py-3 sm:py-4 rounded-2xl bg-gradient-to-r from-emerald-900/40 to-green-900/40 border border-emerald-500/30 hover:border-emerald-400/50 transition-all">
          <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-emerald-500/10 to-green-500/10 opacity-0 group-hover:opacity-100 transition-opacity" />
          <div className="relative flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 flex items-center justify-center shadow-lg shadow-emerald-500/20 flex-shrink-0">
              <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div className="text-left flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-emerald-400 font-bold text-lg sm:text-xl">{apyText}</span>
                <span className="text-xs text-emerald-300 bg-emerald-500/30 px-2 py-0.5 rounded-full font-medium whitespace-nowrap">Revenue Sharing</span>
              </div>
              <p className="text-gray-400 text-xs sm:text-sm mt-1">Stake $RYS and earn daily rewards from platform revenue</p>
            </div>
            <div className="text-emerald-400 group-hover:translate-x-1 transition-transform flex-shrink-0 hidden sm:block">
              →
            </div>
          </div>
        </div>
      </a>

      {/* --- Vision Tagline --- */}
      <p
        className="mt-8 sm:mt-10 mb-12 sm:mb-16 max-w-xl mx-auto text-xs sm:text-sm text-gray-500 leading-relaxed px-4 animate-fade-in"
        style={{ 
          animationDelay: '1.2s',
          animationDuration: '1s',
          animationFillMode: 'both',
          WebkitBackfaceVisibility: 'hidden',
        }}
      >
        Building tools that connect automation, analytics, and community — the
        foundation for Solana's next wave of innovation.
      </p>

    </section>
  );
}
